// App version + changelog entries. Bump CURRENT_VERSION when shipping notable features.
// Members see a "What's New" popup when their lastSeenVersion < CURRENT_VERSION.

export const CURRENT_VERSION = '0.15.0';

export const CHANGELOG = [
  {
    version: '0.15.0',
    date: '2026-06-09',
    title: 'Smarter hold coaching + hands-free hold flow',
    items: [
      'Hold exercises (planks, wall sits, dead hangs) can flow hands-free: after a hold and its rest, the next set auto-starts on a short countdown you can cancel.',
      'The coach now reads your whole last session, not just your final set. It anchors on your typical hold, and when your holds were all over the place it tells you to even them out before adding time.',
      "Tapping the coach's Apply now confirms with \"Applied\", and the coach tip steps aside once you start logging.",
      'Fixed a stray "0" that showed above the coach tip on hold/bodyweight exercises.',
    ],
  },
  {
    version: '0.14.1',
    date: '2026-06-09',
    title: 'Reliable set logging and workout-screen polish',
    items: [
      'Logging a set now reliably saves your weight and reps even when you enter them quickly back to back, and the Log button always marks the set done.',
      'The feedback button moved to the bottom-right and is back on the workout screen, so you can flag anything wrong the moment it happens.',
      'The rest-timer bar no longer cuts off the next-up label when a rest runs overdue.',
    ],
  },
  {
    version: '0.14.0',
    date: '2026-05-30',
    title: 'Inline Exercise Create + Isometric Hold Timer',
    items: [
      "Can't find your machine mid-workout? Create it on the spot from the search box. It's added to your current session immediately and your weights save for next time.",
      'Gym owners see member-created exercises in a promote queue at Exercise Library, then fill in the real equipment and muscle to make them gym-wide.',
      'Isometric exercises (planks, wall sits, dead hangs) now get a dedicated hold timer with a countdown, screen stay-awake, and an audio cue when your hold is done. Set one Hold target and it applies to every set.',
      'Rest timer now beeps each of the final 3 seconds so you can get back to the bar without watching the clock.',
      'Reorder exercises mid-workout with up/down arrows, and Swap now warns before it clears any sets you already logged.',
      'Convert workout to another gym is clearer about when it applies: it shows on saved routines and tells you when a workout is already built for the gym you picked.',
      "Add exercise mid-workout now opens a full-screen picker, so the results list isn't squeezed between the keyboard and the rest timer.",
    ],
  },
  {
    version: '0.13.0',
    date: '2026-05-25',
    title: 'Equipment Scan Rebuild — AI Pre-Fills the Manual Form',
    items: [
      'AI scan now pre-fills an editable form instead of locking the suggestion — fix typos, swap the equipment match, edit exercise names before confirming',
      'Inline "Add new equipment ID" form — extend the dictionary without a deploy when AI finds a machine no one has catalogued yet',
      'Library-aware annotations — each suggested exercise shows "✓ already in library" / "⚠ similar exists" / "+ new" so you see what would actually get created',
      'Per-card disabled reason text — no more silently grayed-out buttons',
      'Animated spinners + sticky page-level loading/error banners so async actions never look frozen',
    ],
  },
  {
    version: '0.12.0',
    date: '2026-05-24',
    title: 'AI Equipment Recognition',
    items: [
      'Snap a photo of any gym machine — AI identifies it and adds it to your equipment list',
      'Owner approval queue at Settings → Equipment Scans: review, approve, or reject scans',
      'Duplicate detection: warns when the scan matches equipment already in your inventory',
      'Auto-suggested new exercises: pick which ones become custom exercises for your gym',
      'Travel-gym + Quick Workout overhaul shipped earlier this week — Routines, Hotel/Garage/Outdoor presets',
      'Streak rework: weekly workout + food log + weigh-in streaks (replaced daily-workout)',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-05-19',
    title: 'Smart Nutrition & Barcode Scanner',
    items: [
      'Barcode scanner — scan food packages for instant macro lookup via Open Food Facts',
      'Adaptive calorie targets — your macro targets now auto-adjust weekly based on your actual TDEE',
      'AI nutrition coaching — weekly insights like "protein under target 4 of 7 days"',
      'Frequent meals — save meals for one-tap logging with saved macros (no re-analysis)',
      'Progress page redesign — PRs organized by body part, sessions load by week',
      'e1RM trend charts inside each exercise card',
      'Additional workouts available after completing your programmed session',
    ],
  },
  {
    version: '0.10.1',
    date: '2026-05-19',
    title: 'Achievements, Rewards & Progress Redesign',
    items: [
      'Achievements system — 17 milestone badges across bronze, silver, gold, and platinum tiers',
      'Weekly consistency streaks — hit your workout target each week to build streaks and earn bonus points',
      'Revised point economy — weekly consistency rewarded more than daily streaks',
      'Progress page redesign — PRs organized by body part with collapsible sections',
      'Recent sessions load by week with "Show previous week" button',
      'e1RM trend charts now inside each exercise card instead of a separate section',
      'Additional workouts available after completing your programmed session',
      'AI coaching hides "maintain" cards and auto-dismisses applied advice',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-05-19',
    title: 'Gym Profiles, Draft Programs & Smart Weights',
    items: [
      'Gym profiles — select your gym (Planet Fitness, LA Fitness, etc.) and the AI uses your actual equipment',
      'Draft programs — generate up to 3 AI programs, compare side by side, activate the best one',
      'Convert workout — one-tap swap all exercises to match a different gym or home equipment',
      'Smart weight recommendations — AI snaps to your gym\'s actual available weights and increments',
      'Home gym equipment checklist — pick exactly what you have, set dumbbell/barbell weight ranges',
      'In-app feedback widget — report bugs or request features without leaving the app',
      'Rest timer persists when you navigate away and turns red when overdue',
      'Add extra sets to any exercise mid-workout',
      'Exercise library now at 322 exercises with full metadata',
    ],
  },
  {
    version: '0.9.1',
    date: '2026-05-18',
    title: 'Exercise Library Expansion',
    items: [
      'Exercise library expanded to 331 exercises (was 108) — nearly 3× more variety',
      'Full metadata on every exercise — instructions, coaching cues, and search terms',
      'New equipment coverage: TRX, resistance bands, EZ-bar, trap bar, landmine, and more',
      'Exercise picker now has muscle group filter tabs for faster browsing',
      'AI programs now have significantly more exercise variety to choose from',
      'Program history — view past programs with stats, compare side by side',
      'Dismissable AI coaching recommendations — "Not now" per card or dismiss all',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-05-18',
    title: 'Messaging, Compliance & Data Export',
    items: [
      'In-app messaging — chat with your trainer directly inside TrainForge',
      'Trainer compliance dashboard — see which members are on track at a glance',
      'Training status — pause training for vacation/injury, auto-complete when programs finish',
      'Download your data — Strong-compatible CSV export of all workouts, measurements, and PRs',
      'Message pagination — load older messages in long conversations',
      'Exercise library expanded to 167 exercises with full Planet Fitness coverage',
    ],
  },
  {
    version: '0.8.0',
    date: '2026-05-17',
    title: 'Templates, Workout Controls & Analytics',
    items: [
      'Workout templates — save any exercise combo and reuse it with one tap',
      'Pause, resume, and cancel workouts with a visible duration timer',
      'Active workout bar across all pages so you never lose track',
      'Quick workout sections start collapsed for faster browsing',
      'Rest timer sits above the bottom nav (no more hiding)',
      '167 exercises now in the library (up from 116) with full Planet Fitness coverage',
      'Week start preference — choose Monday or Sunday in Profile → Options',
      'Feature adoption tracking — gym owners see which features members actually use',
      'Stale workouts auto-abandoned after 3 hours',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-05-16',
    title: 'Smarter Programs, Diet & Recovery',
    items: [
      'Diet & nutrition step in onboarding — tell us your diet type, allergies, and preferences',
      'Automatic deload weeks — every 4th week reduces volume and weight for recovery',
      'AI programs now show the full exercise schedule before you start (not just a summary)',
      'Progressive loading with live status while your program generates',
      'Activity level setting — adjusts calorie targets from sedentary to very active',
      'Canary rollout — gym owners can release features to a % of members before going gym-wide',
      'Faster initial load — app code split from 406KB to 235KB',
      'Thumbs up/down feedback on generated programs to help improve AI quality',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-05-03',
    title: 'TrainForge AI Coach + Daily Check-in',
    items: [
      'AI Coach reviews your week and gives personalized feedback on your Today page',
      'Daily macro recommendations based on your goal and body weight',
      'Suggested program adjustments (deload, intensify, swap) with reasoning',
      'Announcements system — gym owner can post alerts you see on login',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-05-03',
    title: 'Trainers, Video Demos & Booking',
    items: [
      'Trainer dashboard: view assigned members, generate programs on their behalf',
      'Exercise demo videos — owner/trainer can upload per exercise, members see "Demo" button during workouts',
      'Book 1-on-1 sessions with trainers',
      'Trainer availability + time-off management',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-05-03',
    title: 'Smart Workouts & Coach Button',
    items: [
      'Weight prefill: "Use last", "Coach: +5lbs", "Use PR weight" buttons per exercise',
      'Auto-filled weights shown in italic so you know what\'s suggested vs what you typed',
      'Rest timer with countdown between sets + vibrate at zero',
      '"Train Anyway" — pick any workout from your program on rest days or missed days',
      '56 exercises now in the library (doubled from 24)',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-05-03',
    title: 'Progress Tracking & PRs',
    items: [
      'Personal Records automatically detected when you beat a previous best',
      'Progress page: bodyweight chart, e1RM trends, 30-day streak calendar',
      'Body measurements logging (weight, body fat %, skeletal muscle)',
      'Points + streak system with per-set, per-PR, per-day bonuses',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-05-03',
    title: 'AI Program Generation',
    items: [
      'Tell the AI your goals, body type, and preferred schedule → get a full program',
      'Programs use only exercises your gym actually has',
      'Trainers and owners can generate on a member\'s behalf',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-05-03',
    title: 'Launch — Workout Logging',
    items: [
      'Log sets, reps, and weight for each exercise',
      'Today page shows your scheduled workout',
      'Admin can invite members and build manual programs',
      'Dark-themed, mobile-first design',
    ],
  },
];
